import * as Sentry from '@sentry/react'

/**
 * Initialise Sentry for the browser.
 *
 * Fully inert unless a DSN is configured AND this is a production build
 * (`import.meta.env.PROD`), so `pnpm dev` never sends events. Calling
 * `Sentry.init` installs global `window.onerror` / `unhandledrejection`
 * handlers, so once this runs every uncaught error and rejection across every
 * screen is captured automatically.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn || !import.meta.env.PROD) return

  const tracesSampleRate = Number(
    import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.1,
  )
  const replaysSessionSampleRate = Number(
    import.meta.env.VITE_SENTRY_REPLAY_SESSION_RATE ?? 0.1,
  )

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    integrations: [
      // Release Health: tracks sessions so Sentry can compute Crash Free
      // Sessions / Crash Free Users for each release. Requires `release` to be
      // set (injected by the CI build). Crash-Free Users is attributed via the
      // Sentry.setUser() call wired from the auth store. (v9+ replaced the old
      // `autoSessionTracking` option with this integration.)
      Sentry.browserSessionIntegration(),
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Masking is OFF globally: replays record real text and media so they
        // are fully legible. NOTE: this means customer PII (contact emails and
        // names, campaign copy, the signed-in user's details) is sent to and
        // retained by Sentry. To protect a specific sensitive element, add the
        // `sentry-mask` class (text) or `sentry-block` class (media) to it.
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate,
    // Record a replay for a small % of sessions, but always when an error fires.
    replaysSessionSampleRate,
    replaysOnErrorSampleRate: 1.0,
  })
}

/**
 * Attach (or clear) the signed-in user on the Sentry scope so every error and
 * replay is attributed to them. No-op when Sentry isn't initialised.
 */
export function setSentryUser(
  user: { id: number | string; email: string } | null,
): void {
  if (user) {
    Sentry.setUser({ id: String(user.id), email: user.email })
  } else {
    Sentry.setUser(null)
  }
}
